/**
 * SPA error reporting — wires the unified shell to Sentry.
 *
 * Mirrors apps/experts-web/src/observability/sentry.ts. WHY IT EXISTS: the
 * 2026-06-09 prod login outage was invisible because the SPA had a DSN but no
 * Sentry init — login bounced silently (handleRedirectPromise returned no
 * account, no exception). MSAL redirect/silent is exactly the failure class that
 * ships green canaries while no user can log in, so the auth flow MUST be
 * observable from the first paint.
 *
 * All functions are FAIL-SAFE: a Sentry problem (bad DSN, network, SDK error)
 * must never break the app. Everything is wrapped + no-ops without a DSN.
 */
import * as Sentry from '@sentry/react';
import type { AuthConfig } from '@/auth/config';

let initialized = false;

/** Initialise Sentry from the resolved config. No-op without a DSN. */
export function initSentry(config: AuthConfig): void {
  try {
    const dsn = config.sentryDsn?.trim();
    if (!dsn) return; // no DSN configured → nothing to report to
    Sentry.init({
      dsn,
      environment: config.environment || 'unknown',
      // Conservative: capture errors + a slice of traces. No session replay
      // (PII risk — this shell will host chat/CRM customer data).
      tracesSampleRate: 0.1,
      sendDefaultPii: false,
    });
    initialized = true;
  } catch {
    /* never let observability break startup */
  }
}

/**
 * Record an auth-flow anomaly that does NOT throw — e.g. we returned from the
 * Entra redirect but ended up with no active account (the silent login bounce).
 * Captured with structured context so the exact cause is visible instead of the
 * user just landing back on /signin.
 */
export function captureAuthAnomaly(
  message: string,
  context: Record<string, unknown>,
): void {
  try {
    // Always log to console too, so it's visible even before/without Sentry.
    console.error(`[auth-anomaly] ${message}`, context);
    if (!initialized) return;
    Sentry.captureMessage(`auth: ${message}`, {
      level: 'error',
      tags: { subsystem: 'auth', anomaly: 'login_no_account' },
      extra: context,
    });
  } catch {
    /* fail-safe */
  }
}
